import { getInstanceId } from '@va/dashboard/selectors/app';
import { useTranslate } from '@va/localization';
import { Where } from '@va/types/filters';
import { DateRange, PaginatedResponse } from '@va/types/requests';
import { TimePeriod } from '@va/types/time';
import { RawTimeseriesResponsePayload } from '@va/types/time-series';
import { CHART_COLORS_ARRAY } from '@va/ui/design-system';
import { chartHelpers } from '@va/util/helpers';
import { useLazyDataFetch } from '@va/util/hooks';
import { RequestCanceler, useCancelOnUnmount } from '@va/util/misc';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { fetchSessionMetrics, SessionMetrics, SessionMetricsResponse } from './sessions.api';
import { fetchVisitMetrics, VisitMetric } from './visits.api';

type AverageMetricConfig = {
  metric: SessionMetrics;
  overallMetric: SessionMetrics;
  itemKey: string;
  trace: string;
};

export type AverageMetricHookParams = {
  dateRange: DateRange;
  unit: TimePeriod;
  where?: Where;
};

const buildAverageMetricTimeseries = <M extends string>(
  response: { payload?: Array<Partial<Record<M, number | null>> & { date?: string }>; overall?: number | null },
  {
    metric,
    itemKey,
    unit,
    until,
    translate,
  }: {
    metric: M;
    itemKey: string;
    unit: TimePeriod;
    until: string;
    translate: (key: string) => string;
  },
) => {
  const payload = response.payload ?? [];
  const overall = response.overall ?? 0;

  const rawPayload: RawTimeseriesResponsePayload = {
    labels: payload.map((item) => item.date ?? ''),
    datasets: [
      {
        label: itemKey,
        data: payload.map((item) => Number(item[metric] ?? 0)),
        total: Number(overall),
      },
    ],
  };

  return chartHelpers.buildBaseTimeseriesResponse([rawPayload], {
    extractDatasetKey: () => itemKey,
    getDatasetColor: () => CHART_COLORS_ARRAY[0],
    getDatasetLabel: (dataset) => translate(`chart.linechart.label.${dataset.label}`),
    unit,
    until,
  });
};

const useAverageMetricRequest = ({
  config,
  dateRange,
  unit,
  where,
}: { config: AverageMetricConfig } & AverageMetricHookParams) => {
  const translate = useTranslate();
  const websiteId = useSelector(getInstanceId);

  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetchFunc = useCallback(
    async (_: string, websiteId: string, dateRange: DateRange, unit: TimePeriod, where: Where | undefined) => {
      const signal = RequestCanceler.onRequestStart(requestId);
      const response = await fetchSessionMetrics(
        websiteId,
        { trace: config.trace },
        {
          metrics: [config.metric, config.overallMetric],
          dimensions: ['date'],
          granularity: unit,
          dateRange,
          where,
        },
        signal,
      );
      RequestCanceler.onRequestEnd(requestId);

      return response;
    },
    [config, requestId],
  );

  const mapper = useCallback(
    (
      response: PaginatedResponse<SessionMetricsResponse<typeof config.metric | typeof config.overallMetric, 'date'>[]>,
    ) =>
      buildAverageMetricTimeseries(
        { payload: response.payload, overall: response.meta?.[config.overallMetric] },
        {
          metric: config.metric,
          itemKey: config.itemKey,
          unit,
          until: dateRange.end,
          translate,
        },
      ),
    [config, dateRange.end, translate, unit],
  );

  return useLazyDataFetch(
    [`/analytics/sessions`, websiteId, dateRange, unit, where, config.itemKey],
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      onError: () => {
        RequestCanceler.onRequestEnd(requestId);
      },
    },
    mapper,
    fetchFunc,
  );
};

const SESSIONS_PER_VISITOR_CONFIG: AverageMetricConfig = {
  metric: 'averageSessionsPerVisitor',
  overallMetric: 'overallAverageSessionsPerVisitor',
  itemKey: 'averageSessionsPerVisitor',
  trace: 'sessions-per-visitor',
};

const VISITS_PER_SESSION_CONFIG: AverageMetricConfig = {
  metric: 'averageVisitsPerSession',
  overallMetric: 'overallAverageVisitsPerSession',
  itemKey: 'averageVisitsPerSession',
  trace: 'visits-per-session',
};

const SESSION_DURATION_CONFIG: AverageMetricConfig = {
  metric: 'averageSessionDuration',
  overallMetric: 'overallAverageSessionDuration',
  itemKey: 'averageSessionDuration',
  trace: 'session-duration',
};

const BOUNCE_RATE_CONFIG: AverageMetricConfig = {
  metric: 'bounceRate',
  overallMetric: 'overallBounceRate',
  itemKey: 'bounceRate',
  trace: 'bounce-rate',
};

const ADVANCED_BOUNCE_RATE_CONFIG: AverageMetricConfig = {
  metric: 'advancedBounceRate',
  overallMetric: 'overallAdvancedBounceRate',
  itemKey: 'advancedBounceRate',
  trace: 'advanced-bounce-rate',
};

export const useAverageSessionsPerVisitor = (params: AverageMetricHookParams) =>
  useAverageMetricRequest({ config: SESSIONS_PER_VISITOR_CONFIG, ...params });

export const useAverageVisitsPerSession = (params: AverageMetricHookParams) =>
  useAverageMetricRequest({ config: VISITS_PER_SESSION_CONFIG, ...params });

export const useAverageSessionDuration = (params: AverageMetricHookParams) =>
  useAverageMetricRequest({ config: SESSION_DURATION_CONFIG, ...params });

export const useBounceRate = (params: AverageMetricHookParams) =>
  useAverageMetricRequest({ config: BOUNCE_RATE_CONFIG, ...params });

export const useAdvancedBounceRate = (params: AverageMetricHookParams) =>
  useAverageMetricRequest({ config: ADVANCED_BOUNCE_RATE_CONFIG, ...params });

const ACTIVE_VIEWPORT_DURATION_METRIC: SessionMetrics = 'averageSessionActiveViewportDuration';
const ACTIVE_VIEWPORT_DURATION_OVERALL_METRIC: SessionMetrics = 'overallAverageSessionActiveViewportDuration';

export const useAverageActiveViewportDuration = ({ dateRange, unit, where }: AverageMetricHookParams) => {
  const translate = useTranslate();
  const websiteId = useSelector(getInstanceId);

  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetchFunc = useCallback(
    async (_url: string, websiteId: string, dateRange: DateRange, unit: TimePeriod, where: Where | undefined) => {
      const signal = RequestCanceler.onRequestStart(requestId);
      const response = await fetchSessionMetrics(
        websiteId,
        { trace: 'active-viewport-duration' },
        {
          metrics: [ACTIVE_VIEWPORT_DURATION_METRIC, ACTIVE_VIEWPORT_DURATION_OVERALL_METRIC],
          dimensions: ['date'],
          granularity: unit,
          dateRange,
          where,
        },
        signal,
      );
      RequestCanceler.onRequestEnd(requestId);

      return response;
    },
    [requestId],
  );

  const mapper = useCallback(
    (
      response: PaginatedResponse<
        SessionMetricsResponse<
          typeof ACTIVE_VIEWPORT_DURATION_METRIC | typeof ACTIVE_VIEWPORT_DURATION_OVERALL_METRIC,
          'date'
        >[]
      >,
    ) =>
      buildAverageMetricTimeseries(
        { payload: response.payload, overall: response.meta?.[ACTIVE_VIEWPORT_DURATION_OVERALL_METRIC] },
        {
          metric: ACTIVE_VIEWPORT_DURATION_METRIC,
          itemKey: ACTIVE_VIEWPORT_DURATION_METRIC,
          unit,
          until: dateRange.end,
          translate,
        },
      ),
    [dateRange.end, translate, unit],
  );

  return useLazyDataFetch(
    [`/analytics/visits`, websiteId, dateRange, unit, where, ACTIVE_VIEWPORT_DURATION_METRIC],
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      onError: () => {
        RequestCanceler.onRequestEnd(requestId);
      },
    },
    mapper,
    fetchFunc,
  );
};

type VisitAverageMetricConfig = {
  metric: VisitMetric;
  /**
   * When the API has no overall variant for the metric, the total is fetched
   * with a separate request without the `date` dimension.
   */
  overallMetric?: VisitMetric;
  itemKey: string;
  trace: string;
};

const useVisitAverageMetricRequest = ({
  config,
  dateRange,
  unit,
  where,
}: { config: VisitAverageMetricConfig } & AverageMetricHookParams) => {
  const translate = useTranslate();
  const websiteId = useSelector(getInstanceId);

  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetchFunc = useCallback(
    async (_: string, websiteId: string, dateRange: DateRange, unit: TimePeriod, where: Where | undefined) => {
      const signal = RequestCanceler.onRequestStart(requestId);
      const { metric, overallMetric, trace } = config;

      const [timeseriesResponse, overallResponse] = await Promise.all([
        fetchVisitMetrics(
          websiteId,
          { trace },
          {
            metrics: overallMetric ? [metric, overallMetric] : [metric],
            dimensions: ['date'],
            granularity: unit,
            dateRange,
            where,
          },
          signal,
        ),
        overallMetric
          ? undefined
          : fetchVisitMetrics(
              websiteId,
              { trace: `${trace}-overall` },
              { metrics: [metric], dateRange, where },
              signal,
            ),
      ]);
      RequestCanceler.onRequestEnd(requestId);

      return {
        payload: timeseriesResponse.payload,
        overall: overallMetric ? timeseriesResponse.meta?.[overallMetric] : overallResponse?.payload?.[0]?.[metric],
      };
    },
    [config, requestId],
  );

  const mapper = useCallback(
    (response: Awaited<ReturnType<typeof fetchFunc>>) =>
      buildAverageMetricTimeseries(response, {
        metric: config.metric,
        itemKey: config.itemKey,
        unit,
        until: dateRange.end,
        translate,
      }),
    [config, dateRange.end, translate, unit],
  );

  return useLazyDataFetch(
    [`/analytics/visits`, websiteId, dateRange, unit, where, config.itemKey],
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      onError: () => {
        RequestCanceler.onRequestEnd(requestId);
      },
    },
    mapper,
    fetchFunc,
  );
};

const SCROLL_DEPTH_CONFIG: VisitAverageMetricConfig = {
  metric: 'averageScrollDepth',
  overallMetric: 'overallAverageScrollDepth',
  itemKey: 'averageScrollDepth',
  trace: 'scroll-depth',
};

const CONTINUATION_RATE_CONFIG: VisitAverageMetricConfig = {
  metric: 'continuationRate',
  itemKey: 'continuationRate',
  trace: 'continuation-rate',
};

export const useAverageScrollDepth = (params: AverageMetricHookParams) =>
  useVisitAverageMetricRequest({ config: SCROLL_DEPTH_CONFIG, ...params });

export const useContinuationRate = (params: AverageMetricHookParams) =>
  useVisitAverageMetricRequest({ config: CONTINUATION_RATE_CONFIG, ...params });
